'use client';

import Link from 'next/link';
import { useTheme } from 'next-themes';
import {
  Home,
  LayoutGrid,
  Smartphone,
  Info,
  Truck,
  Mail,
  UserRound,
  ClipboardList,
  ShoppingBag,
  LogIn,
  LogOut,
  Sun,
  Moon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { useSessionSummary } from '@/hooks/useSessionSummary';
import { cn } from '@/lib/utils';

/**
 * Derives avatar initials from the OAuth full name, falling back to the
 * email prefix. Shared seam for the drawer shell and both header auth
 * controls (issues 02/03) so all three stay consistent.
 */
export function getAccountInitials(fullName: string | null | undefined, email: string): string {
  const name = (fullName ?? '').trim();
  if (name.length > 0) {
    const parts = name.split(/\s+/);
    if (parts.length >= 2 && parts[0].length > 0 && parts[1].length > 0) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const prefix = email.split('@')[0] ?? '';
  return prefix.slice(0, 2).toUpperCase() || 'ز';
}

interface AccountDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Optional override for the cart entry. When provided (headers that own
   * the OrderPanel open state), the cart row becomes a button that closes
   * the drawer and delegates to the existing cart collection surface.
   * Otherwise it links to `/order`.
   */
  onCartNavigate?: () => void;
}

const PUBLIC_LINKS = [
  { href: '/', label: 'الرئيسية', icon: Home },
  { href: '/products', label: 'جميع المنتجات', icon: LayoutGrid },
  { href: '/devices', label: 'الموديلات', icon: Smartphone },
  { href: '/about', label: 'من نحن', icon: Info },
  { href: '/shipping', label: 'التوصيل', icon: Truck },
  { href: '/contact', label: 'تواصل معنا', icon: Mail },
] as const;

const CUSTOMER_LINKS = [
  { href: '/profile', label: 'حسابي', icon: UserRound },
  { href: '/profile', label: 'طلباتي', icon: ClipboardList },
] as const;

export function AccountDrawer({ open, onOpenChange, onCartNavigate }: AccountDrawerProps) {
  const { summary } = useSessionSummary();
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const close = () => onOpenChange(false);

  const handleCartClick = () => {
    close();
    onCartNavigate?.();
  };

  const displayName = summary?.fullName?.trim() || summary?.email.split('@')[0] || '';
  const initials = getAccountInitials(summary?.fullName, summary?.email ?? '');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-sm">
        <SheetHeader className="border-b border-border">
          <div className="flex flex-col items-center gap-2 px-6 py-2 text-center">
            {summary ? (
              <>
                <Link href="/profile" onClick={close} aria-label="الملف الشخصي">
                  <span
                    aria-hidden
                    className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-base font-bold text-primary-foreground"
                  >
                    {initials}
                  </span>
                </Link>
                <span className="min-w-0">
                  <SheetTitle className="truncate text-base font-bold">{displayName}</SheetTitle>
                  <span className="block truncate text-xs font-medium text-muted-foreground" dir="ltr">
                    {summary.email}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2 text-xs font-semibold"
                  render={<Link href="/auth/sign-out" onClick={close} />}
                >
                  <LogOut className="h-4 w-4" />
                  <span>تسجيل الخروج</span>
                </Button>
              </>
            ) : (
              <>
                <span
                  aria-hidden
                  className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground"
                >
                  <UserRound className="h-6 w-6" />
                </span>
                <span className="min-w-0">
                  <SheetTitle className="text-base font-bold">أهلاً بك في ValleyTech</SheetTitle>
                  <span className="block text-xs font-medium text-muted-foreground">
                    سجّل الدخول لإدارة طلباتك
                  </span>
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2 text-xs font-semibold"
                  render={<Link href="/login" onClick={close} />}
                >
                  <LogIn className="h-4 w-4" />
                  <span>تسجيل الدخول</span>
                </Button>
              </>
            )}
          </div>
        </SheetHeader>

        <nav aria-label="روابط الحساب" className="flex-1 overflow-y-auto px-4 py-3">
          {summary ? (
            <>
              <p className="px-3 pt-1 pb-2 text-xs font-bold text-muted-foreground">حسابي</p>
              <ul className="space-y-1">
                {CUSTOMER_LINKS.map(({ href, label, icon: Icon }) => (
                  <li key={label}>
                    <Link
                      href={href}
                      onClick={close}
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-foreground transition-colors hover:bg-muted"
                    >
                      <Icon className="h-5 w-5 text-muted-foreground" />
                      <span>{label}</span>
                    </Link>
                  </li>
                ))}
                <li>
                  {onCartNavigate ? (
                    <button
                      type="button"
                      onClick={handleCartClick}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-foreground transition-colors hover:bg-muted"
                    >
                      <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                      <span>سلة المشتريات</span>
                    </button>
                  ) : (
                    <Link
                      href="/order"
                      onClick={close}
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-foreground transition-colors hover:bg-muted"
                    >
                      <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                      <span>سلة المشتريات</span>
                    </Link>
                  )}
                </li>
              </ul>
              <div className="mx-3 my-3 border-t border-border" role="separator" />
            </>
          ) : null}

          <p className="px-3 pt-1 pb-2 text-xs font-bold text-muted-foreground">تصفح المتجر</p>
          <ul className="space-y-1">
            {PUBLIC_LINKS.map(({ href, label, icon: Icon }) => (
              <li key={href + label}>
                <Link
                  href={href}
                  onClick={close}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-foreground transition-colors hover:bg-muted"
                >
                  <Icon className="h-5 w-5 text-muted-foreground" />
                  <span>{label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <SheetFooter className="gap-3 border-t border-border">
          <div className="flex items-center justify-between rounded-xl bg-muted/50 px-3 py-2.5">
            <span className="flex items-center gap-2 text-sm font-bold">
              <Sun className="h-4 w-4 dark:hidden" aria-hidden />
              <Moon className="hidden h-4 w-4 dark:block" aria-hidden />
              <span>المظهر الداكن</span>
            </span>
            <Switch
              checked={isDark}
              onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
              aria-label="تبديل المظهر"
            />
          </div>

          <div className="flex items-center justify-center gap-4 text-xs font-semibold text-muted-foreground">
            <Link href="/shipping" onClick={close} className="transition-colors hover:text-foreground">
              المساعدة
            </Link>
            <span aria-hidden className="text-border">
              |
            </span>
            <Link href="/contact" onClick={close} className="transition-colors hover:text-foreground">
              تواصل معنا
            </Link>
            <span aria-hidden className="text-border">
              |
            </span>
            <Link
              href="/privacy"
              onClick={close}
              className={cn('underline underline-offset-4 transition-colors hover:text-foreground')}
            >
              الخصوصية
            </Link>
          </div>

          <p className="text-center text-[11px] font-semibold text-muted-foreground">
            © {new Date().getFullYear()} ValleyTech. جميع الحقوق محفوظة.
          </p>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
